import { useState } from 'react';

import { Box, Button, Link, TableCell, TableRow, Tooltip, Typography } from '@mui/material';

import { useSnackbar } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { fDateTime } from 'src/utils/format-time';
import { ICatalogPdfJob } from 'src/types';

import CatalogPdfStatusBadge from './catalog-pdf-status-badge';

type Props = {
  row: ICatalogPdfJob;
  onRetry: () => void;
  retryDisabled: boolean;
};

const parseDispositionFilename = (header: string | null): string | null => {
  if (!header) return null;
  const match = header.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ?? null;
};

export default function CatalogPdfTableRow({ row, onRetry, retryDisabled }: Props) {
  const { t } = useLocales('catalogPdf');
  const { enqueueSnackbar } = useSnackbar();
  const [downloading, setDownloading] = useState(false);

  // Browsers ignore the server's Content-Disposition filename for
  // cross-origin downloads (Vercel host → CloudFront host) and fall back
  // to the URL's last path segment ("catalogo-latest.pdf"). To honor
  // Antonio's request that the file save as "catalogo-natives-wine-<year>.pdf",
  // we fetch the PDF as a blob (same origin to the `blob:` scheme), then
  // trigger a download with an explicit filename — taken from the
  // response's Content-Disposition when present, with a year-stamped
  // fallback so the file is always named meaningfully.
  const handleDownload = async () => {
    if (!row.download_url || downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(row.download_url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const filename =
        parseDispositionFilename(response.headers.get('content-disposition')) ??
        `catalogo-natives-wine-${new Date().getFullYear()}.pdf`;
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      enqueueSnackbar(t('generate_error'), { variant: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const triggerLabel = row.trigger_type === 'cron' ? t('trigger_cron') : t('trigger_manual');

  const requestedByLabel = (() => {
    if (row.trigger_type === 'cron') return t('system');
    if (row.requested_by_name) return row.requested_by_name;
    if (row.requested_by) return t('user_id', { id: row.requested_by });
    return '—';
  })();

  const countsLabel = row.data_snapshot_counts
    ? t('counts_summary', {
        territories: row.data_snapshot_counts.territories,
        producers: row.data_snapshot_counts.producers,
        wines: row.data_snapshot_counts.wines,
      })
    : '—';

  return (
    <TableRow hover>
      <TableCell>
        <Typography variant="body2">{fDateTime(row.requested_at)}</Typography>
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CatalogPdfStatusBadge status={row.status} />
          {row.status === 'failed' && row.error_message && (
            <Tooltip title={`${t('error_label')}: ${row.error_message}`} arrow>
              <Box component="span" sx={{ display: 'inline-flex', color: 'error.main' }}>
                <Iconify icon="eva:alert-circle-fill" width={16} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </TableCell>

      <TableCell>
        <Typography variant="body2">{triggerLabel}</Typography>
      </TableCell>

      <TableCell>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {requestedByLabel}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {countsLabel}
        </Typography>
      </TableCell>

      <TableCell align="right">
        {row.status === 'done' && row.download_url && (
          <Link
            component="button"
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              typography: 'body2',
              fontWeight: 'fontWeightMedium',
            }}
          >
            <Iconify icon="eva:download-fill" width={16} />
            {downloading ? t('generating') : t('download_link')}
          </Link>
        )}
        {row.status === 'failed' && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={onRetry}
            disabled={retryDisabled}
            startIcon={<Iconify icon="eva:refresh-fill" />}
          >
            {t('retry')}
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
